import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { DndContext, KeyboardSensor, MeasuringStrategy, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableItem } from '@/features/common/sortable/SortableItem';
import SortableOverlay from '@/features/common/sortable/SortableOverlay';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightAddon,
  Spacer,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { BsPlus } from 'react-icons/bs';
import { MdOutlineTableChart } from 'react-icons/md';
import { v4 as uuid } from 'uuid';
import { Select } from 'chakra-react-select';
import { chakraStyles } from '@/features/common/select/styles';
import { useGetFormQuery, useCreateFormMutation, useUpdateFormMutation, useModalitiesQuery } from '@/app/services/nucleus';
import { setNotificationLogMessage } from '@/features/notification/notificationSlice';
import AlertComponent from '@/features/common/AlertComponent';
import LoaderOverlay from '@/features/common/LoaderOverlay';
import ModalComponent from '@/features/common/ModalComponent';
import FormFieldCard from './FormFieldCard';

const PreviewField = ({ field, options = [] }) => {
  const [value, setValue] = useState('');
  const [selected, setSelected] = useState([]);

  switch (field.field_type) {
    case 'string':
      return (
        <Input size="sm" focusBorderColor="brand.700" value={value} onChange={(e) => setValue(e.target.value)} placeholder={`Enter ${field.label}`} />
      );
    case 'number':
      return <Input size="sm" focusBorderColor="brand.700" type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="0" />;
    case 'percentage':
      return (
        <InputGroup size="sm">
          <Input focusBorderColor="brand.700" type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="0" />
          <InputRightAddon>%</InputRightAddon>
        </InputGroup>
      );
    case 'multi_select':
      return (
        <Select
          size="sm"
          chakraStyles={chakraStyles}
          options={options}
          value={selected}
          onChange={setSelected}
          isMulti
          isClearable
          placeholder="Select options..."
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
        />
      );
    default:
      return <Input size="sm" isDisabled />;
  }
};

const slugPreview = (name) =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-');

const makeBlankField = () => ({
  id: uuid(),
  _isNew: true,
  key: '',
  label: '',
  field_type: null,
  options: [],
  required: false,
  modality_ids: [],
});

const EditForm = ({ isNew = false }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(false);
  const [fields, setFields] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const { data: form, isLoading: isFormLoading, isError: isFetchError, error: fetchError } = useGetFormQuery(slug, { skip: isNew });
  const { data: modalities } = useModalitiesQuery();

  const [createForm, { isLoading: isCreating, isError: isCreateError, error: createError, reset: resetCreate }] = useCreateFormMutation();
  const [updateForm, { isLoading: isUpdating, isError: isUpdateError, error: updateError, reset: resetUpdate }] = useUpdateFormMutation();

  const isSubmitting = isCreating || isUpdating;

  const modalityOptions = useMemo(() => (modalities?.items || []).map((m) => ({ value: m.id, label: m.short_name })), [modalities?.items]);

  // Populate state from fetched form (edit mode)
  useEffect(() => {
    if (form) {
      setName(form.name ?? '');
      setFields(
        (form.fields || []).map((f) => ({
          ...f,
          // Normalize string fields at the API boundary so downstream code
          // (validate, generateKey, etc.) can safely call .trim() without guards.
          label: f.label ?? '',
          key: f.key ?? '',
          // id is a local-only React/DnD handle; the stable backend identifier is `key`.
          // buildPayload strips id before sending to the API.
          id: uuid(),
          _isNew: false,
        })),
      );
    }
  }, [form]);

  const [openFieldId, setOpenFieldId] = useState(null);
  const [modalityFilter, setModalityFilter] = useState(null);

  const filteredFields = modalityFilter ? fields.filter((f) => (f.modality_ids || []).includes(modalityFilter)) : fields;

  const handleAddField = () => {
    const newField = makeBlankField();
    setFields((prev) => [...prev, newField]);
    setOpenFieldId(newField.id);
    setModalityFilter(null);
  };

  const handleUpdateField = (updatedField) => {
    setFields((prev) => prev.map((f) => (f.id === updatedField.id ? updatedField : f)));
    if (fieldErrors[updatedField.id]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[updatedField.id];
        return next;
      });
    }
  };

  const handleDeleteField = (id) => {
    setPendingDeleteId(id);
    onDeleteOpen();
  };

  const handleConfirmDeleteField = () => {
    if (openFieldId === pendingDeleteId) setOpenFieldId(null);
    setFields((prev) => prev.filter((f) => f.id !== pendingDeleteId));
    setPendingDeleteId(null);
    onDeleteClose();
  };

  const validate = () => {
    let valid = true;
    if (!name.trim()) {
      setNameError(true);
      valid = false;
    }
    const errors = {};
    fields.forEach((f) => {
      if (!f.label.trim() || !f.field_type) {
        errors[f.id] = true;
        valid = false;
      }
    });
    setFieldErrors(errors);
    const firstInvalidId = fields.find((f) => errors[f.id])?.id;
    if (firstInvalidId) setOpenFieldId(firstInvalidId);
    return valid;
  };

  const buildPayload = () => ({
    name: name.trim(),
    fields: fields.map(({ id, _isNew, ...rest }) => ({
      ...rest,
      options: rest.field_type === 'multi_select' ? rest.options : null,
    })),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    resetCreate();
    resetUpdate();

    const payload = buildPayload();

    if (isNew) {
      createForm(payload)
        .unwrap()
        .then(() => {
          dispatch(setNotificationLogMessage({ msg: 'Form created successfully', status: 'success' }));
          navigate('/forms');
        })
        .catch((err) => console.error('Error creating form', err));
    } else {
      updateForm({ slug, ...payload })
        .unwrap()
        .then(() => {
          dispatch(setNotificationLogMessage({ msg: 'Form updated successfully', status: 'success' }));
          navigate('/forms');
        })
        .catch((err) => console.error('Error updating form', err));
    }
  };

  const pendingDeleteField = fields.find((f) => f.id === pendingDeleteId);
  const [sortItem, setSortItem] = useState(null);
  const dragOverlayField = sortItem ? fields.find((f) => f.id === sortItem.id) ?? null : null;

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  return (
    <VStack spacing={4} alignItems="stretch">
      <Box as="header">
        <Flex align="center">
          <Heading as="h4" size="md" fontWeight="bold" color="brand.900">
            <HStack>
              <MdOutlineTableChart />
              <Text>{isNew ? 'New Form' : `Edit Form`}</Text>
            </HStack>
          </Heading>
          <Spacer />
          <HStack>
            <Button onClick={() => navigate('/forms')} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="outline" onClick={onPreviewOpen} isDisabled={fields.length === 0}>
              Preview
            </Button>
            <Button variant="purple" onClick={handleSubmit} isLoading={isSubmitting}>
              Save
            </Button>
          </HStack>
        </Flex>
      </Box>

      {isFetchError && <AlertComponent status="error" title="Error loading form" description={fetchError?.data?.detail ?? 'Could not load form'} />}
      {isCreateError && <AlertComponent status="error" title="Error creating form" description={createError?.data?.detail ?? 'An error occurred'} />}
      {isUpdateError && <AlertComponent status="error" title="Error updating form" description={updateError?.data?.detail ?? 'An error occurred'} />}

      <Box position="relative" width={{ base: '100%', md: '70%' }}>
        <LoaderOverlay loading={isFormLoading || isSubmitting} />

        {/* Form name */}
        <FormControl isInvalid={nameError} mb={6}>
          <Flex align="center" gap={4}>
            <Text fontSize="sm" fontWeight="medium" w="110px" flexShrink={0} color="gray.600">
              Name
            </Text>
            <Input
              focusBorderColor="brand.700"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError(false);
              }}
              placeholder="e.g. Work History"
            />
          </Flex>
          <FormErrorMessage ml="126px">Name is required.</FormErrorMessage>
        </FormControl>

        {/* Slug preview (new mode only) */}
        {isNew && name.trim() && (
          <Flex align="center" gap={4} mb={6}>
            <Text fontSize="sm" fontWeight="medium" w="110px" flexShrink={0} color="gray.600">
              Slug preview
            </Text>
            <Text fontFamily="mono" fontSize="sm" color="gray.500">
              {slugPreview(name)}
            </Text>
          </Flex>
        )}

        {/* Existing slug (edit mode) */}
        {!isNew && form?.slug && (
          <Flex align="center" gap={4} mb={6}>
            <Text fontSize="sm" fontWeight="medium" w="110px" flexShrink={0} color="gray.600">
              Slug
            </Text>
            <Text fontFamily="mono" fontSize="sm" color="gray.500">
              {form.slug}
            </Text>
          </Flex>
        )}
      </Box>

      {/* Fields section */}
      <Box>
        <Flex align="center" mb={4}>
          <Text fontWeight="semibold" color="gray.700">
            Fields {fields.length > 0 && (modalityFilter ? `(${filteredFields.length} of ${fields.length})` : `(${fields.length})`)}
          </Text>
          <Spacer />
          <HStack spacing={3}>
            <Box w="200px">
              <Select
                size="sm"
                chakraStyles={chakraStyles}
                options={modalityOptions}
                value={modalityOptions.find((o) => o.value === modalityFilter) ?? null}
                onChange={(opt) => setModalityFilter(opt?.value ?? null)}
                isClearable
                isSearchable
                placeholder="Filter by modality"
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 200 }) }}
              />
            </Box>
            <Tooltip hasArrow label="Add a new field">
              <Button leftIcon={<BsPlus />} size="sm" variant="purple" onClick={handleAddField}>
                Add Field
              </Button>
            </Tooltip>
          </HStack>
        </Flex>

        {Object.keys(fieldErrors).length > 0 && (
          <AlertComponent status="warning" title="Validation" description="Each field requires a label and field type." mb={3} />
        )}

        {fields.length === 0 && (
          <Box border="2px dashed" borderColor="gray.200" borderRadius="md" p={8} textAlign="center" color="gray.400">
            <Text>No fields added yet. Click &quot;Add Field&quot; to get started.</Text>
          </Box>
        )}
        {fields.length > 0 && filteredFields.length === 0 && (
          <Box border="2px dashed" borderColor="gray.200" borderRadius="md" p={8} textAlign="center" color="gray.400">
            <Text>No fields match the selected modality.</Text>
          </Box>
        )}

        {modalityFilter ? (
          filteredFields.map((field) => (
            <Box key={field.id} {...(fieldErrors[field.id] ? { outline: '2px solid', outlineColor: 'red.300' } : {})} borderRadius="md">
              <FormFieldCard
                field={field}
                isOpen={openFieldId === field.id}
                onToggle={() => setOpenFieldId(openFieldId === field.id ? null : field.id)}
                modalityOptions={modalityOptions}
                onUpdate={handleUpdateField}
                onDelete={() => handleDeleteField(field.id)}
                draggable={false}
              />
            </Box>
          ))
        ) : (
          <DndContext
            sensors={sensors}
            measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
            onDragStart={({ active }) => setSortItem(active)}
            onDragEnd={({ active, over }) => {
              if (over && active.id !== over.id) {
                const activeIndex = fields.findIndex(({ id }) => id === active.id);
                const overIndex = fields.findIndex(({ id }) => id === over.id);
                setFields(arrayMove(fields, activeIndex, overIndex));
              }
              setSortItem(null);
            }}
            onDragCancel={() => setSortItem(null)}
          >
            <SortableContext items={fields} strategy={verticalListSortingStrategy}>
              {fields.map((field) => (
                <SortableItem key={field.id} id={field.id}>
                  <Box {...(fieldErrors[field.id] ? { outline: '2px solid', outlineColor: 'red.300' } : {})} borderRadius="md">
                    <FormFieldCard
                      field={field}
                      isOpen={openFieldId === field.id}
                      onToggle={() => setOpenFieldId(openFieldId === field.id ? null : field.id)}
                      modalityOptions={modalityOptions}
                      onUpdate={handleUpdateField}
                      onDelete={() => handleDeleteField(field.id)}
                    />
                  </Box>
                </SortableItem>
              ))}
            </SortableContext>
            <SortableOverlay>
              {dragOverlayField ? (
                <FormFieldCard
                  field={dragOverlayField}
                  isOpen={false}
                  onToggle={() => {}}
                  modalityOptions={modalityOptions}
                  onUpdate={() => {}}
                  onDelete={() => {}}
                />
              ) : null}
            </SortableOverlay>
          </DndContext>
        )}
      </Box>

      {/* Form preview */}
      <ModalComponent size="md" title={name || 'Form Preview'} secondaryText="Close" isOpen={isPreviewOpen} onClose={onPreviewClose} isError={false}>
        <VStack spacing={4} align="stretch">
          {fields.map((field) => (
            <FormControl key={field.id} isRequired={field.required}>
              <FormLabel fontSize="sm" mb={1}>
                {field.label || 'Unlabeled Field'}
              </FormLabel>
              <PreviewField field={field} options={(field.options || []).map((o) => ({ value: o, label: o }))} />
            </FormControl>
          ))}
        </VStack>
      </ModalComponent>

      {/* Delete field confirmation */}
      <ModalComponent
        size="sm"
        title="Delete Field"
        primaryText="Yes"
        secondaryText="No"
        handleConfirm={handleConfirmDeleteField}
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        isError={false}
      >
        <Text>
          Are you sure you want to delete <strong>{pendingDeleteField?.label || 'this field'}</strong>? This will take effect when you save.
        </Text>
      </ModalComponent>
    </VStack>
  );
};

EditForm.propTypes = {
  isNew: PropTypes.bool,
};

export default EditForm;
